import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { openPaytmCheckout } from "@/lib/paytm";

type CheckoutPayload =
  | { kind: "cashfree_session"; paymentSessionId: string }
  | { kind: "razorpay_order"; orderId: string; keyId: string; amountPaise: number }
  | {
      kind: "paytm_checkout_js";
      host: string;
      mid: string;
      orderId: string;
      txnToken: string;
      amountRupees: number;
    }
  | { kind: "redirect"; url: string }
  | { kind: "demo"; amountRupees: number };

interface CreatePaymentOrderResponse {
  manual: boolean;
  gatewayOrderId?: string;
  checkout?: CheckoutPayload;
}

export type PaymentOutcome =
  // No gateway configured -- the booking is already confirmed server-side. Nothing left to do.
  | { kind: "manual_confirmed" }
  // Razorpay/Paytm are in-page overlays, not a redirect -- once they resolve, the caller
  // must navigate to the confirmation page itself (webhook-driven polling decides the real
  // outcome, same as every other gateway).
  | { kind: "needs_confirmation_redirect" }
  // Cashfree's redirectTarget "_self" and the generic "redirect" kind both navigate the
  // browser away directly -- the caller should do nothing further; there may be no page left
  // to do it in by the time this resolves.
  | { kind: "navigating_away" }
  // No real gateway configured, DEMO_MODE is on -- render an in-page "simulate payment" step
  // instead of a real checkout. See components/DemoPaymentStep.tsx.
  | { kind: "demo_pending"; gatewayOrderId: string; amountRupees: number };

/**
 * Starts payment for an already-held booking (appointment or batch enrollment -- both create
 * a `bookings` row the same shape, see createPaymentOrder.ts, which is booking-kind-agnostic).
 * Shared by BookingFlow and the batches enrollment flow so the gateway-branching logic exists
 * in exactly one place, not duplicated per booking type.
 */
export async function startPaymentForBooking(params: {
  bookingId: string;
  holdToken: string;
  description: string;
  customerEmail: string;
  customerPhone: string;
}): Promise<PaymentOutcome> {
  const createPaymentOrder = httpsCallable<
    { bookingId: string; holdToken: string },
    CreatePaymentOrderResponse
  >(functions, "createPaymentOrder");
  const result = await createPaymentOrder({
    bookingId: params.bookingId,
    holdToken: params.holdToken,
  });

  if (result.data.manual) {
    return { kind: "manual_confirmed" };
  }

  const checkout = result.data.checkout!;
  if (checkout.kind === "demo") {
    return {
      kind: "demo_pending",
      gatewayOrderId: result.data.gatewayOrderId!,
      amountRupees: checkout.amountRupees,
    };
  }

  if (checkout.kind === "cashfree_session") {
    const { load } = await import("@cashfreepayments/cashfree-js");
    const cashfree = await load({
      mode: process.env.NEXT_PUBLIC_CASHFREE_ENV === "production" ? "production" : "sandbox",
    });
    await cashfree?.checkout({
      paymentSessionId: checkout.paymentSessionId,
      redirectTarget: "_self",
    });
    return { kind: "navigating_away" };
  }

  if (checkout.kind === "razorpay_order") {
    await openRazorpayCheckout(checkout, {
      name: "Hoop & Yoga Fitness",
      description: params.description,
      prefillEmail: params.customerEmail,
      prefillContact: params.customerPhone,
    });
    return { kind: "needs_confirmation_redirect" };
  }

  if (checkout.kind === "paytm_checkout_js") {
    await openPaytmCheckout(checkout);
    return { kind: "needs_confirmation_redirect" };
  }

  // An external gateway URL, not an internal Next.js route -- router.push doesn't apply.
  // Navigates away immediately; nothing meaningful to return to the caller after this.
  window.location.href = checkout.url;
  return { kind: "navigating_away" };
}
